import { useState, useEffect, useCallback, useRef } from 'react';
import { getCache, setCache, fetchWithCache, subscribe } from '../lib/cache';

/**
 * useDataCache - Reusable SWR data fetching and caching hook for React 18/19
 *
 * @param {string|null|false} key - Deterministic cache key (or falsy to pause fetching)
 * @param {Function} fetcher - Async fetcher function returning data or Axios response
 * @param {Object} options - Configuration options
 * @param {number} [options.ttl] - Custom TTL in ms (defaults to route-level TTL)
 * @param {boolean} [options.revalidateOnMount=true] - Whether to revalidate in background on mount
 * @param {any} [options.initialData=null] - Default fallback data
 * @param {Function} [options.onSuccess] - Callback when fresh data arrives
 * @param {Function} [options.onError] - Callback on fetch failure
 */
export function useDataCache(key, fetcher, options = {}) {
  const {
    ttl,
    revalidateOnMount = true,
    initialData = null,
    onSuccess,
    onError,
  } = options;

  // Stable references for callbacks to avoid re-triggering effects
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // Synchronous cache hydration on mount (Pass 1 render)
  const [state, setState] = useState(() => {
    if (!key) {
      return { data: initialData, loading: false, error: null, isValidating: false };
    }
    const cached = getCache(key);
    if (cached.exists) {
      return {
        data: cached.data !== undefined ? cached.data : initialData,
        loading: false,
        error: null,
        isValidating: cached.isStale,
      };
    }
    return {
      data: initialData,
      loading: true,
      error: null,
      isValidating: true,
    };
  });

  // Track active key across renders
  const prevKeyRef = useRef(key);

  // Sync state if key changes dynamically
  if (prevKeyRef.current !== key) {
    prevKeyRef.current = key;
    if (!key) {
      setState({ data: initialData, loading: false, error: null, isValidating: false });
    } else {
      const cached = getCache(key);
      if (cached.exists) {
        setState({
          data: cached.data !== undefined ? cached.data : initialData,
          loading: false,
          error: null,
          isValidating: cached.isStale,
        });
      } else {
        setState({
          data: initialData,
          loading: true,
          error: null,
          isValidating: true,
        });
      }
    }
  }

  // Subscribe to external cache updates & handle background revalidations
  useEffect(() => {
    if (!key || typeof fetcherRef.current !== 'function') return;

    let isMounted = true;

    // 1. Subscribe to pub/sub cache events for this key
    const unsubscribe = subscribe(key, (freshData) => {
      if (isMounted) {
        setState(prev => ({
          ...prev,
          data: freshData,
          loading: false,
          error: null,
        }));
      }
    });

    // 2. Evaluate if revalidation is required
    const cached = getCache(key);
    const shouldFetch = !cached.exists || cached.isStale || revalidateOnMount;

    if (shouldFetch) {
      setState(prev => ({ ...prev, isValidating: true }));

      fetchWithCache(key, () => fetcherRef.current(), {
        forceRevalidate: cached.isStale,
        ttl,
      })
        .then((freshData) => {
          if (isMounted) {
            setState({
              data: freshData,
              loading: false,
              error: null,
              isValidating: false,
            });
            onSuccessRef.current?.(freshData);
          }
        })
        .catch((err) => {
          if (isMounted) {
            setState(prev => ({
              ...prev,
              loading: false,
              isValidating: false,
              error: err,
            }));
            onErrorRef.current?.(err);
          }
        });
    }

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [key, ttl, revalidateOnMount]);

  // Optimistic mutation & manual revalidation
  const mutate = useCallback(async (newData, shouldRevalidate = true) => {
    if (!key) return;

    let computedData = newData;
    if (typeof newData === 'function') {
      const cached = getCache(key);
      const current = cached.exists ? cached.data : state.data;
      computedData = newData(current);
    }

    if (computedData !== undefined) {
      setCache(key, computedData, ttl);
      setState(prev => ({ ...prev, data: computedData, error: null }));
    }

    if (shouldRevalidate && typeof fetcherRef.current === 'function') {
      setState(prev => ({ ...prev, isValidating: true }));
      try {
        const fresh = await fetchWithCache(key, () => fetcherRef.current(), {
          forceRevalidate: true,
          ttl,
        });
        setState({ data: fresh, loading: false, error: null, isValidating: false });
        return fresh;
      } catch (err) {
        setState(prev => ({ ...prev, isValidating: false, error: err }));
        throw err;
      }
    }
    return computedData;
  }, [key, ttl, state.data]);

  // Manual revalidate trigger
  const revalidate = useCallback(async () => {
    if (!key || typeof fetcherRef.current !== 'function') return;
    setState(prev => ({ ...prev, isValidating: true }));
    try {
      const fresh = await fetchWithCache(key, () => fetcherRef.current(), {
        forceRevalidate: true,
        ttl,
      });
      setState({ data: fresh, loading: false, error: null, isValidating: false });
      return fresh;
    } catch (err) {
      setState(prev => ({ ...prev, isValidating: false, error: err }));
      throw err;
    }
  }, [key, ttl]);

  return {
    data: state.data,
    loading: state.loading,
    error: state.error,
    isValidating: state.isValidating,
    mutate,
    revalidate,
  };
}

export default useDataCache;
